import type { NavigateStore } from '~/stores/navigate';
import type { NavigateProps } from 'react-router-dom';

import { DEFAULT_LOCATION_BACK, SEARCH_PARAM_KEYS } from '~/constants/navigate';
import { useLocation, useNavigate } from '@remix-run/react';
import useNavigateStore from '~/stores/navigate';

export type NavigateOptions = {
  setting?: Omit<NavigateProps, 'to'>;
  session?: string;
  fallbackUrl?: string;
  autoReplace?: boolean;
  params?: Record<string, string>;
  isOffSetCacheLocation?: boolean;
};

export function useHandleNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  const cacheLocation = useNavigateStore((s) => s.cacheLocation);
  const urlSearchParams = useNavigateStore((s) => s.urlSearchParams);
  const setCacheLocation = useNavigateStore((s) => s.setCacheLocation);

  // ------------------- MAIN FUNCTION -------------------
  const handleNavigate = (to: string | number, options?: NavigateOptions) => {
    const params = options?.params;
    const destination = to;

    const oldLocation = cacheLocation;
    handleCacheLocation(options);

    // Handle numeric destination by cache location
    if (typeof destination === 'number') {
      if (!oldLocation) {
        navigate(options?.fallbackUrl || DEFAULT_LOCATION_BACK);
        return;
      }

      navigate(destination);
      return;
    }

    // Handle for url not exist params shop
    const isExistParamsShop = new URLSearchParams(location.search ?? {}).has('shop');
    if (!isExistParamsShop) {
      return handleIsExistParamsShop({ destination, options });
    }

    const isAutoReplace = options?.autoReplace;
    if (isAutoReplace) return;

    const combinedSearch = getCombinedSearchByParams(params);
    navigate(destination + combinedSearch, options?.setting);
  };

  // ------------------- HELPER FUNCTION -------------------
  const getDefaultQueryStringByCache = (options?: NavigateOptions) => {
    const queryParams = new URLSearchParams(urlSearchParams || {});
    if (options?.session) {
      queryParams.set('session', options?.session);
    }

    return queryParams.toString();
  };

  /**
   * Get combined search by params
   * @param params
   * @param isExistParamsShop
   * @returns ?{key1=value1&key2=value2}
   */
  const getCombinedSearchByParams = (
    params?: NavigateOptions['params'],
    isExistParamsShop = true,
  ): string => {
    const queryString = getDefaultQueryStringByCache();
    let combinedSearch = isExistParamsShop ? handleFilterSearchParams() : queryString;

    // Handle custom params
    if (params && Object.keys(params).length > 0) {
      const customParam = new URLSearchParams(params).toString() ?? '';
      combinedSearch = combinedSearch ? `${combinedSearch}&${customParam}` : customParam;
    }

    return `?${combinedSearch}`;
  };

  /**
   * Get default location back
   * @returns string
   */
  const getDefaultLocationBack = () => {
    const locationSearch = new URLSearchParams(location.search ?? {});
    const type = locationSearch.get('type');
    switch (type) {
      default:
        return DEFAULT_LOCATION_BACK;
    }
  };

  /**
   * Get data for history back
   * @param options
   * @returns {
   *    destination: string,
   *    params?: Record<string, string>
   * }
   */
  const getDataForHistoryBack = ({
    oldLocation,
    options,
  }: {
    oldLocation: NavigateStore['cacheLocation'];
    options?: NavigateOptions;
  }) => {
    let destination = oldLocation?.pathname
      ? oldLocation.pathname
      : options?.fallbackUrl || getDefaultLocationBack();

    let params = undefined;

    if (destination === location.pathname && options?.fallbackUrl) {
      destination = options?.fallbackUrl || getDefaultLocationBack();
    }
    const oldParamsSearch = new URLSearchParams(oldLocation?.search ?? {});
    if (oldParamsSearch) {
      params = Object.entries(oldParamsSearch).reduce(function (
        acc: Record<string, string>,
        [key, value],
      ) {
        acc[key] = value;
        return acc;
      }, {});
    }

    return {
      destination,
      params,
    };
  };

  const handleIsExistParamsShop = ({
    destination,
    options,
  }: {
    destination: string;
    options?: NavigateOptions;
  }) => {
    const combinedSearch = getCombinedSearchByParams(options?.params, false) || '';
    const endpoint = `${destination}${combinedSearch}`;

    navigate(endpoint, options?.setting);
    return false;
  };

  const handleCacheLocation = (options?: NavigateOptions) => {
    const isAutoReplace = options?.autoReplace;
    if (!isAutoReplace && !options?.isOffSetCacheLocation) {
      setCacheLocation({
        pathname: location.pathname,
        search: location.search,
      });
    }

    return false;
  };

  /**
   * Filter search params
   * @returns String clear cache params
   */
  const handleFilterSearchParams = () => {
    const currentSearch = location.search;
    const queryCurrentSearch = new URLSearchParams(currentSearch ?? {});

    Array.from(queryCurrentSearch.keys()).forEach((key) => {
      if (!SEARCH_PARAM_KEYS.includes(key)) {
        queryCurrentSearch.delete(key);
      }
    });

    return queryCurrentSearch.toString();
  };

  const changeNavigateForNumericDestination = (destination: string | number): boolean => {
    if (typeof destination !== 'number' || destination !== -1) {
      return false;
    }

    navigate(destination);
    return false;
  };

  return {
    handleNavigate,
  };
}
